/* Majova Point – MPCore: config, storage, smoke/QA flags, pricing, formatting, backend post, tracking.
   Vanilla ES2018, no dependencies. Loaded deferred on every page (after mp-consent.js). */
(function (w, d) {
  'use strict';

  var QA_KEY = 'mp_qa';
  var CATALOG_KEY = 'mp_catalog';
  var CATALOG_FRESH_MS = 30 * 60 * 1000;
  var QTY_MAX = 9999;
  // When this page was opened. Kept in memory only; start_order sends how long ago that was.
  var OPENED = Date.now();

  var cfg = w.MP || {};
  // A web-app URL copied while signed in to several Google accounts contains /u/<n>/ (or
  // /a/macros/<domain>/ for Workspace). Anonymous visitors get a Google 404 page there instead of the
  // script, which shows up as a CORS error. The public form is /macros/s/<id>/exec.
  if (typeof cfg.endpoint === 'string') {
    cfg.endpoint = cfg.endpoint.trim().replace(
      /^https:\/\/script\.google\.com\/(?:macros\/u\/\d+|a\/macros\/[^/]+(?:\/u\/\d+)?)\/s\//,
      'https://script.google.com/macros/s/'
    );
  }

  /* ---------------------------------------------------------------- storage */
  // JSON localStorage. Every access is wrapped; if storage throws (private mode, blocked
  // site data) values live in memory for this page view instead. set() returns true only when
  // the value reached localStorage (i.e. it survives the next page load).
  var mem = {};
  var has = function (o, k) { return Object.prototype.hasOwnProperty.call(o, k); };

  var store = {
    get: function (key) {
      var raw;
      try { raw = w.localStorage.getItem(key); } catch (e) { raw = null; }
      if (raw === null || raw === undefined) return has(mem, key) ? mem[key] : null;
      try { return JSON.parse(raw); } catch (e) { return null; }
    },
    set: function (key, value) {
      try {
        w.localStorage.setItem(key, JSON.stringify(value));
        delete mem[key];
        return true;
      } catch (e) {
        mem[key] = value;
        return false;
      }
    },
    del: function (key) {
      delete mem[key];
      try { w.localStorage.removeItem(key); } catch (e) { /* ignore */ }
    }
  };

  /* ------------------------------------------------------------------ utils */
  function param(name) {
    if (!name) return null;
    try { return new URLSearchParams(w.location.search).get(name); } catch (e) { return null; }
  }

  function uuid() {
    var c = w.crypto;
    try { if (c && typeof c.randomUUID === 'function') return c.randomUUID(); } catch (e) { /* fall through */ }
    var b = new Uint8Array(16);
    try { c.getRandomValues(b); } catch (e) {
      for (var i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256);
    }
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    var h = '';
    for (var j = 0; j < 16; j++) {
      h += (b[j] + 0x100).toString(16).slice(1);
      if (j === 3 || j === 5 || j === 7 || j === 9) h += '-';
    }
    return h;
  }

  function round2(n) { return Math.round(n * 100) / 100; }

  // Removes the given query parameters from the address bar (no reload). Every other parameter
  // (gclid, utm_*, preview_theme_id, ...) keeps its exact encoding, and the hash is kept.
  function stripParams(names) {
    names = names.filter(function (n) { return typeof n === 'string' && n !== ''; });
    var h = w.history;
    var loc = w.location;
    if (!names.length || !h || typeof h.replaceState !== 'function' || !loc || !loc.search) return;
    try {
      var parts = String(loc.search).replace(/^\?/, '').split('&');
      var kept = parts.filter(function (part) {
        var name = part.split('=')[0];
        try { name = decodeURIComponent(name.replace(/\+/g, ' ')); } catch (e) { /* keep raw */ }
        return names.indexOf(name) < 0;
      });
      if (kept.length === parts.length) return;
      h.replaceState(h.state, '', loc.pathname + (kept.length ? '?' + kept.join('&') : '') + (loc.hash || ''));
    } catch (e) { /* sandboxed frame etc.: leave the URL as it is */ }
  }

  /* ------------------------------------------------------------ smoke + QA */
  // No visitor or session identifier is created or stored. A visit counts as a smoke visit only
  // while the ad link's marker (?mp=s1) is in the address bar of the current page; it is not
  // remembered anywhere else.
  function smoke() {
    var smokeValue = cfg.smokeValue == null ? '' : String(cfg.smokeValue);
    return smokeValue !== '' && param(cfg.smokeParam) === smokeValue;
  }

  // Milliseconds since this page was opened.
  function pageAge() { return Math.max(0, Date.now() - OPENED); }

  function isQA() { return store.get(QA_KEY) === true; }

  function isBot() {
    try { return w.navigator.webdriver === true; } catch (e) { return false; }
  }

  /* ---------------------------------------------------------------- backend */
  // Resolves with the JSON answer, or null on any failure or after timeoutMs. With keepalive the
  // request is not aborted at the timeout and may finish after the page has been left, so the
  // caller can move on without the request being cancelled (it is sent exactly once either way).
  function post(payload, timeoutMs, keepalive) {
    var url = api.cfg.endpoint;
    if (!url || typeof w.fetch !== 'function') return Promise.resolve(null);
    if (w.Shopify && w.Shopify.designMode) return Promise.resolve(null); // theme editor: no backend writes
    return new Promise(function (resolve) {
      var done = false;
      var ctrl = !keepalive && typeof w.AbortController === 'function' ? new w.AbortController() : null;
      var timer = setTimeout(function () {
        if (ctrl) { try { ctrl.abort(); } catch (e) { /* ignore */ } }
        finish(null);
      }, timeoutMs > 0 ? timeoutMs : 8000);

      function finish(value) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        resolve(value);
      }

      try {
        var init = {
          method: 'POST',
          headers: { 'Content-Type': 'text/plain;charset=utf-8' },
          body: JSON.stringify(payload),
          credentials: 'omit'
        };
        if (ctrl) init.signal = ctrl.signal;
        if (keepalive) init.keepalive = true;
        w.fetch(url, init).then(function (res) { return res.text(); }).then(function (text) {
          var obj = null;
          try { obj = JSON.parse(text); } catch (e) { obj = null; }
          finish(obj && typeof obj === 'object' && !Array.isArray(obj) ? obj : null);
        }, function () { finish(null); });
      } catch (e) {
        finish(null);
      }
    });
  }

  /* --------------------------------------------------------------- tracking */
  function track(event, params) {
    var consent = w.MPConsent;
    try {
      if (!consent || typeof consent.analytics !== 'function' || consent.analytics() !== true) return false;
    } catch (e) {
      return false;
    }
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push(Object.assign({ event: event }, params || {}));
    return true;
  }

  /* ------------------------------------------------------- money + pricing */
  var nfInt = null;
  var nfDec = null;
  try {
    nfInt = new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 0 });
    nfDec = new Intl.NumberFormat('sv-SE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch (e) { /* manual fallback below */ }

  function num(n) {
    n = Number(n);
    if (!isFinite(n)) n = 0;
    var whole = Math.round(n) === round2(n);
    var s;
    if (nfInt && nfDec) {
      s = (whole ? nfInt : nfDec).format(whole ? Math.round(n) : round2(n));
    } else {
      var parts = (whole ? String(Math.round(Math.abs(n))) : Math.abs(n).toFixed(2)).split('.');
      s = (n < 0 ? '-' : '') + parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + (parts[1] ? ',' + parts[1] : '');
    }
    return s.replace(/\s/g, ' ');
  }

  function fmt(n) { return num(n) + ' kr'; }

  function normTiers(tiers) {
    if (!Array.isArray(tiers)) return [];
    return tiers.filter(function (t) {
      return t && isFinite(Number(t.min)) && Number(t.min) >= 1 &&
        t.price !== null && t.price !== '' && isFinite(Number(t.price)) && Number(t.price) >= 0;
    }).map(function (t) {
      return { min: Math.floor(Number(t.min)), price: Number(t.price) };
    }).sort(function (a, b) { return a.min - b.min; });
  }

  function unitPrice(tiers, qty) {
    var list = normTiers(tiers);
    if (!list.length) return null;
    var price = list[0].price;
    for (var i = 0; i < list.length; i++) {
      if (qty >= list[i].min) price = list[i].price;
    }
    return price;
  }

  function shippingCost() {
    var s = Number(api.cfg.shipping);
    return isFinite(s) && s >= 0 ? s : 0;
  }

  // -> {qty, unit, subtotal, shipping, total} or null for an unknown product / invalid qty.
  function calc(product, qty) {
    var q = typeof qty === 'number' ? qty : parseQty(qty);
    if (!product || typeof q !== 'number' || q !== Math.floor(q) || q < 1 || q > QTY_MAX) return null;
    var unit = unitPrice(product.tiers, q);
    if (unit === null) return null;
    var subtotal = round2(unit * q);
    var shipping = shippingCost();
    return { qty: q, unit: unit, subtotal: subtotal, shipping: shipping, total: round2(subtotal + shipping) };
  }

  // -> integer 1..9999 or null. Accepts surrounding/grouping whitespace ("1 000"), digits only.
  function parseQty(str) {
    if (str === null || str === undefined) return null;
    var s = String(str).replace(/[\s  ]+/g, '');
    if (!/^\d{1,6}$/.test(s)) return null;
    var n = parseInt(s, 10);
    return n >= 1 && n <= QTY_MAX ? n : null;
  }

  /* ---------------------------------------------------------------- catalog */
  // Keeps the usable products only (e.g. a block whose prices were cleared is dropped instead of
  // breaking the whole catalog). null when none are left.
  function cleanCatalog(c) {
    if (!c || typeof c !== 'object' || Array.isArray(c)) return null;
    var out = {};
    var n = 0;
    Object.keys(c).forEach(function (k) {
      var p = c[k];
      if (k !== '__proto__' && p && typeof p === 'object' && p.key === k && Array.isArray(p.tiers) && normTiers(p.tiers).length > 0) {
        out[k] = p;
        n++;
      }
    });
    return n ? out : null;
  }

  function catalogEl(doc) {
    return (doc && doc.getElementById && doc.getElementById('mp-catalog')) || null;
  }

  function readCatalog(doc) {
    var el = catalogEl(doc);
    if (!el) return null;
    try {
      return cleanCatalog(JSON.parse(el.textContent));
    } catch (e) {
      return null;
    }
  }

  function cacheCatalog(c) {
    store.set(CATALOG_KEY, { ts: Date.now(), catalog: c });
  }

  // Stored copy as {catalog, fresh}; older than 30 min (or the earlier untimed format) is stale.
  function cachedCatalog() {
    var entry = store.get(CATALOG_KEY);
    var timed = !!entry && typeof entry === 'object' && typeof entry.ts === 'number' && !!entry.catalog;
    var c = cleanCatalog(timed ? entry.catalog : entry);
    if (!c) return null;
    var age = timed ? Date.now() - entry.ts : -1;
    return { catalog: c, fresh: age >= 0 && age < CATALOG_FRESH_MS };
  }

  // In-page #mp-catalog (authoritative when present) > fresh stored copy > fetch('/') >
  // stale stored copy > null.
  function catalog() {
    if (catalogEl(d)) {
      var c = readCatalog(d);
      if (c) cacheCatalog(c);
      return Promise.resolve(c);
    }
    var cached = cachedCatalog();
    if (cached && cached.fresh) return Promise.resolve(cached.catalog);
    var stale = cached ? cached.catalog : null;
    if (typeof w.fetch !== 'function' || typeof w.DOMParser !== 'function') return Promise.resolve(stale);
    var root = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';
    return w.fetch(root, { credentials: 'same-origin' }).then(function (res) {
      return res.ok ? res.text() : '';
    }).then(function (html) {
      var found = html ? readCatalog(new w.DOMParser().parseFromString(html, 'text/html')) : null;
      if (found) cacheCatalog(found);
      return found || stale;
    }).catch(function () { return stale; });
  }

  /* -------------------------------------------------------------------- api */
  var api = {
    cfg: cfg,
    store: store,
    smoke: smoke,
    pageAge: pageAge,
    isQA: isQA,
    isBot: isBot,
    post: post,
    track: track,
    fmt: fmt,
    num: num,
    unitPrice: unitPrice,
    calc: calc,
    catalog: catalog,
    parseQty: parseQty,
    uuid: uuid
  };
  w.MPCore = api;

  /* ------------------------------------------------------------ on page load */
  var qa = param(cfg.qaParam || 'mp_qa');
  if (qa === '1') store.set(QA_KEY, true);
  else if (qa === '0') store.del(QA_KEY);
  if (isQA()) {
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ traffic_type: 'internal' });
  }
  // Housekeeping: our own storage items unused for 30 days are removed, so nothing is kept indefinitely.
  // (mp_consent has its own 365-day rule in mp-consent.js; mp_qa is an internal test flag.)
  var MAX_AGE = 30 * 24 * 60 * 60 * 1000;
  ['mp_order', 'mp_catalog'].forEach(function (k) {
    var v = store.get(k);
    var t = v && typeof v === 'object' ? v.ts : 0;
    if (v !== null && v !== undefined && !(t > Date.now() - MAX_AGE)) store.del(k);
  });
  // Written by earlier builds and no longer used (mp_session held a random session id).
  ['mp_session', 'mp_last_start_order', 'mp_order_sent'].forEach(function (k) { store.del(k); });
  // The QA flag has been read above; keep it out of shared/bookmarked links and later page_location.
  // The smoke marker stays in the address bar: it is how a reload of the ad landing page still
  // counts as a smoke visit, without storing anything.
  stripParams([cfg.qaParam || 'mp_qa']);
})(window, document);
